import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18n-lite'

import { ActiveSessionCard, GoBackButton } from './styles'
import { AuthenticationType } from 'authentication/shared/types'
import Iframe from 'shared/components/Iframe'
import { useCallback, useEffect } from 'react'
import { Icon, Text } from '@yes.technology/react-toolkit'
import { useLogout, useIframe } from 'shared/hooks'
import { useNavigate } from 'react-router-dom'

export function shouldProcessMessage(
  eventOrigin: string,
  expectedOrigin: string
): boolean {
  return eventOrigin === expectedOrigin.replace(/\/$/g, '')
}

type ActiveSessionProps = {
  goBack?: () => void
  close?: () => void
}

const ActiveSession = ({ goBack, close }: ActiveSessionProps) => {
  const { language, setLanguage, t } = useTranslation()

  const { token } = useSelector(
    ({ authentication }: AuthenticationType) => authentication
  )

  const navigate = useNavigate()

  const encodeHref = encodeURIComponent(window.location.href)
  const redirectOrigin = `redirectOrigin=${encodeHref}`
  const waAuthenticationUrl = window.waAuthenticationUrl
  const encodeToken = encodeURIComponent(token)
  const encodeLanguage = encodeURIComponent(language)

  const iframeSrc = `${waAuthenticationUrl}active-session?token=${encodeToken}&lang=${encodeLanguage}`

  const { iframeAttributes, receiveMessage } = useIframe({
    origin: waAuthenticationUrl,
    src: iframeSrc,
    title: 'Active Session',
    initialHeight: '300px'
  })

  const navigateToModifyCredentials = useCallback(() => {
    navigate(
      `/${window.authSitemodelUuid}?${redirectOrigin}&token=${encodeURIComponent(token)}&isModifyCredentials=true`
    )
  }, [navigate, redirectOrigin, token])

  const onReceivedMessage = useCallback(
    (event: MessageEvent) => {
      receiveMessage(event)

      if (!shouldProcessMessage(event.origin, waAuthenticationUrl)) return

      if (event.data.logout) {
        close?.()
        navigate('/logout')
      }

      if (event.data.modifyCredentials) {
        navigateToModifyCredentials()
      }

      if (event.data.language) {
        setLanguage(event.data.language)
      }
    },
    [
      receiveMessage,
      waAuthenticationUrl,
      close,
      navigate,
      navigateToModifyCredentials,
      setLanguage
    ]
  )

  useEffect(() => {
    window.addEventListener('message', onReceivedMessage)
    return () => window.removeEventListener('message', onReceivedMessage)
  }, [onReceivedMessage])

  return (
    <ActiveSessionCard>
      {goBack && (
        <GoBackButton onClick={goBack}>
          <Icon iconName='DropdownArrowLeft' />
          <Text variant='content-emphasis-03'>{t('navigation.back')}</Text>
        </GoBackButton>
      )}
      {iframeAttributes && <Iframe {...iframeAttributes} />}
    </ActiveSessionCard>
  )
}

export default ActiveSession
